"use client";

import { c as _c } from "react/compiler-runtime";
import { IconExternalLink, IconRosetteDiscountCheck, IconX, IconZoomCode } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { tokenizeJson, type JsonToken } from "@/components/domain/registration/json-highlight";
import { Favicon } from "@/components/icons/favicon";
import { Button } from "@domainstack/ui/button";
import { Checkbox } from "@domainstack/ui/checkbox";
import { CopyButton } from "@domainstack/ui/copy-button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@domainstack/ui/dialog";
import { ResponsiveTooltip, ResponsiveTooltipContent, ResponsiveTooltipTrigger } from "@domainstack/ui/responsive-tooltip";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";
import { cn } from "@domainstack/ui/utils";
function withTokenKeys(tokens: JsonToken[]) {
  const seen = new Map<string, number>();
  return tokens.map(token => {
    const baseKey = `${token.type}:${token.value}`;
    const duplicateCount = seen.get(baseKey) ?? 0;
    seen.set(baseKey, duplicateCount + 1);
    return {
      key: `${baseKey}:${duplicateCount}`,
      token
    };
  });
}
function HighlightedLine(t0) {
  const $ = _c(6);
  const {
    line,
    tokens
  } = t0;
  if (!tokens || tokens.length === 0) {
    const t1 = line || "\xA0";
    let t2;
    if ($[0] !== t1) {
      t2 = <>{t1}</>;
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t1;
  if ($[2] !== tokens) {
    t1 = tokens.map(_temp);
    $[2] = tokens;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <>{t1}</>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function _temp(t0) {
  const {
    key,
    token
  } = t0;
  return <span key={key} className={cn(token.type === "key" && "text-blue-700 dark:text-blue-400", token.type === "string" && "text-emerald-700 dark:text-emerald-400", token.type === "number" && "text-amber-700 dark:text-amber-400", token.type === "boolean" && "text-violet-700 dark:text-violet-400", token.type === "null" && "text-stone-500 italic dark:text-stone-400")}>{token.value}</span>;
}
interface RawDataDialogProps {
  domain: string;
  format: string;
  /** Raw data: JSON object for RDAP, plain text string for WHOIS */
  data: Record<string, unknown> | string;
  serverName: string;
  serverUrl: string | undefined;
}
export function RawDataDialog(t0) {
  const $ = _c(68);
  const {
    domain,
    format,
    data,
    serverName,
    serverUrl
  } = t0;
  const [open, setOpen] = useState(false);
  const [wrapLines, setWrapLines] = useState(true);
  let t1;
  bb0: {
    if (typeof data === "string") {
      t1 = data;
      break bb0;
    }
    let t2;
    if ($[0] !== data) {
      t2 = JSON.stringify(data, null, 2);
      $[0] = data;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    t1 = t2;
  }
  const formattedData = t1;
  let t2;
  if ($[2] !== data || $[3] !== formattedData) {
    bb1: {
      const source = formattedData.trim();
      if (typeof data === "string") {
        t2 = source.split("\n").map(_temp2);
        break bb1;
      }
      t2 = tokenizeJson(source).map(_temp4);
    }
    $[2] = data;
    $[3] = formattedData;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const lineItems = t2;
  const t3 = `View raw ${format} data`;
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setOpen(true);
    t5 = <IconZoomCode className="size-4 text-foreground/95" />;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== format) {
    t6 = <span className="sr-only">View raw {format} data</span>;
    $[7] = format;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t6) {
    t7 = <TooltipTrigger render={<Button variant="ghost" size="icon-sm" aria-label={t3} onClick={t4}>{t5}{t6}</Button>} />;
    $[9] = t3;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== format) {
    t8 = <TooltipContent><p>View raw {format} data</p></TooltipContent>;
    $[12] = format;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <DialogTrigger render={<Tooltip>{t7}{t8}</Tooltip>} />;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  let t11;
  if ($[17] !== domain) {
    t10 = <Favicon domain={domain} />;
    t11 = <span className="truncate text-base tracking-[-0.01em] lowercase">{domain}</span>;
    $[17] = domain;
    $[18] = t10;
    $[19] = t11;
  } else {
    t10 = $[18];
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <IconRosetteDiscountCheck className="size-3.5 text-accent-green" />;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== format) {
    t13 = <ResponsiveTooltipTrigger render={<span className="ml-1 flex cursor-default items-center gap-1 text-[13px] font-normal text-foreground/75">{t12}<span>{format}</span></span>} />;
    $[21] = format;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== serverName || $[24] !== serverUrl) {
    t14 = serverUrl ? <a href={serverUrl} target="_blank" rel="noopener" className="flex items-center gap-1 underline underline-offset-2">{serverName}<IconExternalLink className="size-3 -translate-y-[1px]" /></a> : serverName;
    $[23] = serverName;
    $[24] = serverUrl;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t14) {
    t15 = <ResponsiveTooltipContent><span className="flex items-center gap-1 truncate">Verified by{" "}<span className="font-medium">{t14}</span></span></ResponsiveTooltipContent>;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t13 || $[29] !== t15) {
    t16 = <ResponsiveTooltip>{t13}{t15}</ResponsiveTooltip>;
    $[28] = t13;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t16) {
    t17 = <DialogHeader className="place-items-start space-y-1 border-b border-border bg-card/60 p-4"><DialogTitle className="flex items-center gap-2">{t10}{t11}{t16}</DialogTitle></DialogHeader>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  const t18 = `Raw ${format} data`;
  const t19 = wrapLines ? "grid-cols-[auto_1fr]" : "w-max min-w-full grid-cols-[auto_auto]";
  let t20;
  if ($[35] !== t19) {
    t20 = cn("grid", t19);
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== lineItems || $[38] !== wrapLines) {
    let t22;
    if ($[40] !== wrapLines) {
      t22 = item => <div key={item.lineNumber} className="col-span-2 grid grid-cols-subgrid rounded px-1 py-0.5 hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none active:bg-muted/50"><span className="justify-self-end px-1 text-muted-foreground/70 select-none">{item.lineNumber}</span><span className={cn("min-w-0 pr-1 pl-3", wrapLines ? "break-all whitespace-pre-wrap" : "whitespace-pre")}><HighlightedLine line={item.line} tokens={item.tokens} /></span></div>;
      $[40] = wrapLines;
      $[41] = t22;
    } else {
      t22 = $[41];
    }
    t21 = lineItems.map(t22);
    $[37] = lineItems;
    $[38] = wrapLines;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[42] !== t20 || $[43] !== t21) {
    t22 = <div className="p-3"><pre className="font-mono text-xs leading-5 text-foreground/90"><code className={t20}>{t21}</code></pre></div>;
    $[42] = t20;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t18 || $[46] !== t22) {
    t23 = <div tabIndex={0} className="min-h-0 flex-1 overflow-auto overscroll-contain bg-popover/10 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1" aria-label={t18}>{t22}</div>;
    $[45] = t18;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setWrapLines(_temp5);
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== wrapLines) {
    t25 = <Button variant="outline" size="sm" className="gap-2 !px-3 text-[13px]" onClick={t24}><Checkbox checked={wrapLines} className="size-3.5" />Wrap lines</Button>;
    $[49] = wrapLines;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== formattedData) {
    t26 = <CopyButton variant="outline" size="sm" className="gap-2 !px-3 text-[13px]" value={formattedData} showLabel={true} />;
    $[51] = formattedData;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = () => setOpen(false);
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Button variant="outline" size="sm" onClick={t27} className="gap-2 !px-3 text-[13px]"><IconX />Close</Button>;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== t26) {
    t29 = <div className="space-x-2">{t26}{t28}</div>;
    $[55] = t26;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== t25 || $[58] !== t29) {
    t30 = <div className="flex w-full items-center justify-between gap-2 border-t border-border bg-card/60 p-3">{t25}{t29}</div>;
    $[57] = t25;
    $[58] = t29;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== t17 || $[61] !== t23 || $[62] !== t30) {
    t31 = <DialogContent className="gap-0 p-0 sm:max-w-2xl">{t17}{t23}{t30}</DialogContent>;
    $[60] = t17;
    $[61] = t23;
    $[62] = t30;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== open || $[65] !== t31 || $[66] !== t9) {
    t32 = <Dialog open={open} onOpenChange={setOpen}>{t9}{t31}</Dialog>;
    $[64] = open;
    $[65] = t31;
    $[66] = t9;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  return t32;
}
function _temp5(prev) {
  return !prev;
}
function _temp4(tokens, index_0) {
  return {
    lineNumber: index_0 + 1,
    line: tokens.map(_temp3).join(""),
    tokens: withTokenKeys(tokens)
  };
}
function _temp3(token) {
  return token.value;
}
function _temp2(line, index) {
  return {
    lineNumber: index + 1,
    line,
    tokens: null
  };
}